import { CommonService, InvoiceService, ToastMessageService } from '@fitness-force/api';
import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  UntypedFormControl,
} from '@angular/forms';
import {
  ErrorMsgComponent,
} from '@fitness-force/errors';
import {
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { StyleClassModule } from 'primeng/styleclass';
import {
  BehaviorSubject,
  Observable,
} from 'rxjs';
import { Claim, Invoice, WriteOff } from '@fitness-force/models';
import { RadioButtonModule } from 'primeng/radiobutton';
import { PopoverModule } from 'primeng/popover';
import { TableModule } from 'primeng/table';
import { InputNumberModule } from 'primeng/inputnumber';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import { SkeletonModule } from 'primeng/skeleton';


@Component({
  selector: 'write-off',
  templateUrl: './write-off.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    SelectModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    TranslateModule,
    ButtonModule,
    PopoverModule,
    RadioButtonModule,
    TableModule,
    InputNumberModule,
    CurrencyformatPipe,
    ErrorMsgComponent,
    StyleClassModule,
    DateTimeFormatPipe,
    SkeletonModule
  ],
})


export class WriteOffComponent implements OnInit {
  @Input('visible') writeOffModal: boolean = false;
  @Input('showList') showList: boolean = false;
  @Input('invoiceData') invoiceData: Invoice;
  @Input('clientId') clientId: number=0;
  @Input('tenantId') tenantId: number=0;
  @Output() closeDialog = new EventEmitter<{closePopup: boolean, reloadData:boolean}>();
  writeOffList: any[] = [];
  btnLoading = false;
  allClientData$ = new BehaviorSubject<any>({});
  currentStaff$ = new BehaviorSubject<any>({});
  selectedStaff$ = new BehaviorSubject<any>({});
  allStaffs$ = new BehaviorSubject<any>([]);

  updateBillOwnerForm: UntypedFormGroup = new UntypedFormGroup({});
  isClientRepresentative = new BehaviorSubject(true);
  claims: string[] = ['can_do_billing'];
  claim$: Observable<Claim[]> = new Observable();

  writeOffForm: UntypedFormGroup;
  writeOffTableData: WriteOff[] = [];
  totalBalanceDue: number = 0; // This should come from invoice data
  loading: boolean = false;
  showVoidModal: boolean = false;
  VoidWriteOffForm: UntypedFormGroup;
  selectedWriteOff:any;
  dataUpdated:boolean = false;




  constructor(
    public translate: TranslateService,
    private fb: UntypedFormBuilder,
    private invoiceService: InvoiceService,
    private commonService: CommonService,
    private toastMessageService: ToastMessageService,
    public  cdr: ChangeDetectorRef
  ) {
    this.initForm();
    this.initVoidForm();
  }


  private initForm() {
    this.writeOffForm = this.fb.group({
      writeOffType: ['full', Validators.required],
      amount: ['', [Validators.min(0)]],
      reason: ['', Validators.required]
    });
    this.writeOffForm.get('amount')?.disable();
  }


  private initVoidForm() {
    this.VoidWriteOffForm = this.fb.group({
      void_reason: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.componentInit();
    if(this.showList){
      this.loadWriteOffList();
    }
  }


  componentInit() {
    if (this.invoiceData) {
      this.totalBalanceDue = this.invoiceData.total_balance_due_in_minor_currency || 0;
      this.writeOffForm.controls['amount'].addValidators([Validators.max(this.totalBalanceDue)]);
    }
    // Subscribe to writeOffType changes


    this.writeOffForm.get('writeOffType')?.valueChanges.subscribe(type => {
      if (type === 'full') {
        this.writeOffForm.get('amount')?.clearValidators();
        this.writeOffForm.get('amount')?.disable();
      }
      else {
        this.writeOffForm.get('amount')?.addValidators([Validators.required,Validators.max(this.totalBalanceDue)]);
        this.writeOffForm.get('amount')?.updateValueAndValidity();
        this.writeOffForm.get('amount')?.enable();
      }



    });
  }

  loadWriteOffList() {
    this.loading = true;
    this.invoiceService.getWriteOffs(this.tenantId, this.clientId, this.invoiceData.id).subscribe({
      next: (response) => {
        if(response.length > 0){
          // response.forEach((item:any) => {
          //   item.number_prefix = this.invoiceData.number_prefix;
          // });
          this.writeOffTableData = response;
        }
        else{
          this.writeOffTableData = [];
        }
        this.loading = false;
        this.cdr.detectChanges();
      },

      error: (error) => {
        this.toastMessageService.showError(error);
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  submitWriteOff() {
    if (this.writeOffForm.invalid) {
      Object.keys(this.writeOffForm.controls).forEach(key => {
        const control = this.writeOffForm.get(key);
        if (control?.invalid) {
          control.markAsTouched();
        }
      });
      this.toastMessageService.showError(this.translate.instant('WRITE_OFF.ERRORS.FILL_REQUIRED_FIELDS'));
      return;
    }

    const formValue = this.writeOffForm.value;
    if (formValue.amount <= 0 || formValue.amount > this.totalBalanceDue) {
      this.toastMessageService.showError(this.translate.instant('WRITE_OFF.ERRORS.INVALID_AMOUNT'));
      return;
    }

    this.btnLoading = true;
    const writeOffData = {
      amount_in_minor_currency: this.writeOffForm.get('writeOffType')?.value === 'full' ? this.totalBalanceDue : this.commonService.ConvertMinorCurrencyFromAmount(formValue.amount),
      write_off_reason: formValue.reason
    };

    this.invoiceService.WriteOffInvoice(this.tenantId, this.clientId, this.invoiceData.id, writeOffData).subscribe({
      next: () => {
        this.toastMessageService.showSuccess(this.translate.instant('WRITE_OFF.SUCCESS.CREATED'));
        this.resetForm();
        this.loadWriteOffList();
        this.btnLoading = false;
        this.closeDialogEvent(true);
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.toastMessageService.showError(error);
        this.btnLoading = false;
        this.cdr.detectChanges();
      },
      complete: () => {
        this.btnLoading = false;
        this.cdr.detectChanges();
      }
    });

  }

  voidWriteOff(writeOff:any) {
    this.selectedWriteOff = writeOff;
    this.showVoidModal = true;
  }

  deleteWriteOff(form: UntypedFormGroup) {
    if (form.invalid) {
      Object.keys(form.controls).forEach(key => {
        const control = form.get(key);
        if (control?.invalid) {
          control.markAsTouched();  
        }
      });
      this.toastMessageService.showError(this.translate.instant('WRITE_OFF.ERRORS.FILL_REQUIRED_FIELDS'));
      return;
    }
    const payload = {
      void_reason: form.get('void_reason')?.value
    }

    this.btnLoading = true;

    this.invoiceService.voidWriteOff(this.tenantId, this.clientId, this.invoiceData.id, this.selectedWriteOff.write_off_id, payload).subscribe({
      next: () => {
        this.toastMessageService.showSuccess(this.translate.instant('WRITE_OFF.SUCCESS.VOIDED'));
        this.loadWriteOffList();  
        this.btnLoading = false;
        this.showVoidModal = false;
        this.dataUpdated = true;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.toastMessageService.showError(error);
        this.btnLoading = false;  
        this.cdr.detectChanges();
      },
      complete: () => {
        this.btnLoading = false;
        this.cdr.detectChanges();
      }
    });
  }


    private resetForm() {
    this.writeOffForm.reset({
      writeOffType: 'full',
      amount: 0,
      reason: ''
    });
  }

  closeDialogEvent(updated = false) {
    this.writeOffModal = false;
    this.showList = false;
    this.closeDialog.emit({ closePopup: true, reloadData: updated ? true : this.dataUpdated ? true : false });
  }

  get VoidReasonFormControl(): UntypedFormControl {
    return this.VoidWriteOffForm.get('void_reason') as UntypedFormControl;
  }
  
}

